import { useCallback, useState } from 'react';
import type { QRAnswerItem } from '@scripsteam/scrips-signal-ds';
import {
  ENCOUNTER_NARRATIVE_LINK_ID,
  appendToSink,
  readSink,
  sinkAnswer,
  sinkHasLine,
  type OrphanEntry,
} from './orphan-sink';
import type { SinkLinesHost } from './use-sink-offers';

interface ResponseLike {
  answers: QRAnswerItem[];
}

export interface UseAmbientSinkHostOptions<R extends ResponseLike> {
  /** The encounter is signed (PROD-2547 / B5): no offer is kept and nothing is written. */
  locked: boolean;
  /** The rendered note. Used only to say which offers are already in it (`isInNote`). */
  response: R;
  /**
   * The saved note has loaded for THIS encounter (`hydratedFor === encounterId`) and can be saved.
   * False: nothing is written, because the hydrate would overwrite an earlier write.
   */
  writable: boolean;
  /**
   * A FUNCTIONAL update of the note (`setResponse(prev => next)`). The next note is derived from the
   * latest state, never from a copy read at the click, so two fast Accepts, or a typed edit made while
   * an Accept runs, are both kept.
   */
  updateResponse: (update: (prev: R) => R) => void;
  markDirty: () => void;
  /** Resolves true once a note save that carries the line reached the server. */
  whenStored: (carries: (response: R) => boolean) => Promise<boolean>;
}

/** One line per text: the same line twice in one analysis is one offer. */
function distinct(entries: readonly OrphanEntry[]): OrphanEntry[] {
  const seen = new Set<string>();
  return entries.filter((e) => {
    const t = e.text.trim();
    if (t.length === 0 || seen.has(t)) return false;
    seen.add(t);
    return true;
  });
}

/**
 * PROD-3195: the host half of the "Additional notes" offers.
 *
 * Rule (product owner, 15 Oct 2026): "nothing fill by themselves, the owner should add them by
 * himself". So every line ambient recovered for the encounter narrative sink (`onSinkEntries`) is an
 * OFFER. `onSinkEntries` keeps the latest set and writes NOTHING: no append, no dirty mark, no
 * autosave. That holds for a live frame, a re-analysis and a restored analysis after a reload.
 *
 * `host.onAccept` is the only write, and it runs only on the physician's Accept click: it appends that
 * one line to the sink (`appendToSink`, with the "(dictated)" mark and its no-double rule), marks the
 * note dirty and resolves once a save carrying it reached the server.
 *
 * Offers are held in state only. A reload clears the ones nobody decided; the next analysis offers
 * them again, and the decision record hides the ones already decided.
 */
export function useAmbientSinkHost<R extends ResponseLike>({
  locked,
  response,
  writable,
  updateResponse,
  markDirty,
  whenStored,
}: UseAmbientSinkHostOptions<R>) {
  const [offers, setOffers] = useState<readonly OrphanEntry[]>([]);

  /** Every new analysis. Offers only: no write, no dirty mark, so no autosave. */
  const onSinkEntries = useCallback(
    (entries: readonly OrphanEntry[]) => {
      // PROD-2547 (B5): the host refuses too, not only the panel.
      if (locked) return;
      // The latest analysis replaces the earlier offers.
      setOffers(distinct(entries));
    },
    [locked],
  );

  const onAccept = useCallback(
    async (entry: OrphanEntry): Promise<boolean> => {
      if (locked || !writable) return false;
      const text = entry.text.trim();
      if (text.length === 0) return false;
      // A save carries the line only when the sink holds it as a whole line (not a part of a longer one).
      const carries = (r: R) => sinkHasLine(readSink(r.answers), text);
      const stored = whenStored(carries);
      updateResponse((prev) => {
        const existing = readSink(prev.answers);
        const merged = appendToSink(existing, [entry]);
        // ALREADY IN THE SINK (`appendToSink` adds a line once). Most often our own earlier Accept
        // whose save did not confirm: nothing is appended, and the save below reports it, so the
        // decision is recorded.
        if (merged === existing || merged === undefined) return prev;
        return {
          ...prev,
          answers: [...prev.answers.filter((a) => a.linkId !== ENCOUNTER_NARRATIVE_LINK_ID), sinkAnswer(merged)],
        };
      });
      // Marked dirty in both cases, so a save that carries the line does run.
      markDirty();
      return stored;
    },
    [locked, writable, updateResponse, markDirty, whenStored],
  );

  const current = readSink(response.answers);
  const host: SinkLinesHost = {
    offers: locked ? [] : offers,
    canAccept: writable && !locked,
    isInNote: (text) => sinkHasLine(current, text),
    onAccept,
  };

  return { onSinkEntries, host };
}
